import React, { useEffect, useRef, forwardRef, useImperativeHandle, useContext } from 'react';
import { TranslationContext } from './TranslationContext';
import { logger } from '../../utils/logger';

interface TranslationIframeWrapperProps {
  src: string;
  title?: string;
  width?: string | number;
  height?: string | number;
  style?: React.CSSProperties;
  className?: string;
  allowFullScreen?: boolean;
  allow?: string;
  sandbox?: string;
  loading?: 'eager' | 'lazy';
  referrerPolicy?: '' | 'no-referrer' | 'no-referrer-when-downgrade' | 'origin' | 'origin-when-cross-origin' | 'same-origin' | 'strict-origin' | 'strict-origin-when-cross-origin' | 'unsafe-url';
  onLoad?: () => void;
}

/**
 * A wrapper component for iframes that enables translation support
 * This component adds the translation script to the iframe and handles communication
 */
const TranslationIframeWrapper = forwardRef<HTMLIFrameElement, TranslationIframeWrapperProps>(
  function TranslationIframeWrapper(props, ref) {
    const {
      src,
      title,
      width = '100%',
      height = '100%',
      style,
      className,
      allowFullScreen,
      allow,
      sandbox,
      loading,
      referrerPolicy,
      onLoad,
      ...rest
    } = props;
    
    const internalRef = useRef<HTMLIFrameElement>(null);
    
    // Forward the internal ref to the parent component
    useImperativeHandle(ref, () => internalRef.current!);
    
    const context = useContext(TranslationContext);
    
    // Handle the null case with default values
    const { 
      isTranslated = false, 
      currentLanguage = 'EN' 
    } = context || {};
    
    // Function to handle iframe loading
    const handleIframeLoad = () => {
      if (!internalRef.current) return;
      
      logger.info('Iframe loaded:', internalRef.current.src);
      
      // For cross-origin iframes, we need to use postMessage
      if (internalRef.current.contentWindow) {
        try {
          // Send a message to load the translation script
          internalRef.current.contentWindow.postMessage({
            type: 'LOAD_TRANSLATION_SCRIPT',
            scriptUrl: window.location.origin + '/iframe-translation.js'
          }, '*');
          
          logger.info('Sent message to iframe to load translation script');
        } catch (error) {
          logger.error('Error sending load script message to iframe:', error);
        }
      }
    };
    
    // Handle iframe load event
    const handleLoad = () => {
      handleIframeLoad();
      
      // Call the original onLoad handler if provided
      if (onLoad) {
        onLoad();
      }
    };
    
    // Set up message listener for iframe communication
    useEffect(() => {
      // Function to handle messages from the iframe
      const handleIframeMessage = (event: MessageEvent) => {
        // Check if the message is from our iframe
        if (!internalRef.current || event.source !== internalRef.current.contentWindow) {
          return;
        }
        
        // Handle translation ready message
        if (event.data?.type === 'TRANSLATION_READY') {
          logger.info('Iframe is ready for translation:', event.data);
          
          // Send current translation state to the iframe
          sendTranslationState();
        }
      };
      
      // Function to send translation state to the iframe
      const sendTranslationState = () => {
        if (!internalRef.current || !internalRef.current.contentWindow) return;
        
        // Normalize the language code by replacing hyphens with underscores
        const normalizedLanguage = currentLanguage.replace(/-/g, '_');
        
        const message = {
          type: 'TRANSLATE_CONTENT',
          language: normalizedLanguage,
          originalLanguage: currentLanguage, // Include the original language code for reference
          action: isTranslated ? 'translate' : 'revert'
        };
        
        try {
          internalRef.current.contentWindow.postMessage(message, '*');
          logger.info('Sent translation state to iframe:', message);
        } catch (error) {
          logger.error('Error posting message to iframe:', error);
        }
      };
      
      // Add message event listener
      window.addEventListener('message', handleIframeMessage);
      
      // Clean up
      return () => {
        window.removeEventListener('message', handleIframeMessage);
      };
    }, [isTranslated, currentLanguage]);
    
    // Update the iframe when translation state changes
    useEffect(() => {
      if (!internalRef.current) return;
      
      // Function to send translation state to the iframe
      const sendTranslationState = () => {
        if (!internalRef.current || !internalRef.current.contentWindow) return;
        
        // Normalize the language code by replacing hyphens with underscores
        const normalizedLanguage = currentLanguage.replace(/-/g, '_');
        
        const message = {
          type: 'TRANSLATE_CONTENT',
          language: normalizedLanguage,
          originalLanguage: currentLanguage, // Include the original language code for reference
          action: isTranslated ? 'translate' : 'revert'
        };
        
        try {
          internalRef.current.contentWindow.postMessage(message, '*');
          logger.info('Sent translation state to iframe:', message);
        } catch (error) {
          logger.error('Error posting message to iframe:', error);
        }
      };
      
      // Send translation state immediately
      sendTranslationState();
      
      // Also send it after a short delay to ensure the iframe has had time to initialize
      setTimeout(sendTranslationState, 500);
      
      // Set up a MutationObserver to detect when the iframe src changes
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'src') {
            logger.info(`Iframe src changed from ${mutation.oldValue} to ${internalRef.current?.src}`);
            
            // Reset any state related to the previous iframe
            if (internalRef.current?.contentWindow) {
              try {
                // Send a cleanup message to the iframe
                internalRef.current.contentWindow.postMessage({
                  type: 'CLEANUP_TRANSLATION'
                }, '*');
                
                logger.info('Sent cleanup message to iframe before src change');
              } catch (error) {
                logger.error('Error sending cleanup message to iframe:', error);
              }
            }
          }
        });
      });
      
      if (internalRef.current) {
        observer.observe(internalRef.current, { attributes: true, attributeFilter: ['src'] });
      }
      
      // Clean up
      return () => {
        observer.disconnect();
      };
    }, [isTranslated, currentLanguage]);
    
    return (
      <iframe
        ref={internalRef}
        src={src}
        title={title}
        width={width}
        height={height}
        style={style}
        className={className}
        allowFullScreen={allowFullScreen}
        allow={allow}
        sandbox={sandbox}
        loading={loading}
        referrerPolicy={referrerPolicy}
        onLoad={handleLoad}
        {...rest}
      />
    );
  }
);

// Add display name for better debugging
TranslationIframeWrapper.displayName = 'TranslationIframeWrapper';

export default TranslationIframeWrapper;
